/**
 * McpAddServerForm — inline BYO (bring-your-own) MCP server add form
 * rendered by `AgentMcpView` when the user clicks "Add MCP server".
 *
 * Fields:
 *   - Display name (required) — becomes the `agentMcpConnections[<key>]` record
 *     key after slug-normalization + collision-suffix.
 *   - Endpoint URL (required) — an absolute `https://` URL. Plain `http://`
 *     is accepted only for loopback hosts (localhost / 127.0.0.1 / ::1) to
 *     support local-dev stacks; see `isValidMcpEndpoint`.
 *   - Auth type — None / Basic / Key / ManagedServiceIdentity (MSI).
 *     Per-mode fields validate inline.
 *
 * Submit flow:
 *   1. Client-side validation.
 *   2. `listMcpTools({ mcpServerUrl, authentication })` is called as a probe.
 *      If the runtime rejects the credentials/URL, the form stays open and the
 *      error message is surfaced inline after sanitization (URLs stripped,
 *      length-capped via `sanitizeErrorMessage`). We only persist on success.
 *   3. The new record is written to `connections.json.agentMcpConnections` via
 *      `useSaveConnectionsJson` (matches the persistence pattern in
 *      `ConnectionList/useConnectionsEditor.ts`). The record shape mirrors
 *      Logic Apps Standard: `{ mcpServerUrl, displayName, authentication }`.
 *      After saving we merge the just-persisted records into the connection
 *      store baseline so a later publish keeps them.
 *   4. The caller (`AgentMcpView`) reacts via `onAdded(key)` — typically by
 *      switching to the Others tab, auto-expanding the new card, and closing
 *      the form. Cache invalidation is handled by `useSaveConnectionsJson`.
 *
 * Notes for reviewers:
 *   - Phase B2 (full LogicAppsUX wizard parity — tool-selection mode,
 *     custom-headers panel) is deferred. This is the minimum viable BYO add
 *     surface that mirrors the runtime contract.
 *   - We write to `connections.json` (prod), not `connections-draft.json`,
 *     to match the LogicAppsUX wizard semantics: a connection has no draft
 *     concept; once it validates, it's persistent. The picker already merges
 *     draft over saved when listing BYO entries, so a draft-mode user still
 *     sees the new record immediately.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Dropdown,
  Field,
  Input,
  Option,
  Text,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { useAuth } from '@/auth/useAuth';
import { useCurrentProject } from '@/hooks/useCurrentProject';
import { useConnectionsJson, useSaveConnectionsJson } from '@/hooks/useConnections';
import { useAppStore } from '@/stores/appStore';
import { useConnectionStore } from '@/stores/connectionStore';
import { useNotificationStore } from '@/stores/notificationStore';
import { listMcpTools } from '@/services/mcpApi';
import { mcpKeys } from '@/hooks/useMcpServers';
import { type ConnectionsConfiguration, type McpConnection } from '@/types/logicApps';
import { sanitizeErrorMessage } from '@/utils/sanitization';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingVerticalM,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  title: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  helpText: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  actions: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: tokens.spacingHorizontalS,
    marginTop: tokens.spacingVerticalXS,
  },
  errorBanner: {
    padding: tokens.spacingVerticalS,
    paddingLeft: tokens.spacingHorizontalS,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorPaletteRedBackground1,
    color: tokens.colorPaletteRedForeground1,
    fontSize: tokens.fontSizeBase200,
    whiteSpace: 'pre-wrap',
    overflow: 'auto',
    maxHeight: '120px',
  },
  warnBanner: {
    padding: tokens.spacingVerticalS,
    paddingLeft: tokens.spacingHorizontalS,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorPaletteYellowBackground1,
    color: tokens.colorPaletteYellowForeground1,
    fontSize: tokens.fontSizeBase200,
    whiteSpace: 'pre-wrap',
    overflow: 'auto',
    maxHeight: '160px',
  },
});

export type McpAuthMode = 'None' | 'Basic' | 'Key' | 'ManagedServiceIdentity';

const AUTH_MODE_LABELS: Record<McpAuthMode, string> = {
  None: 'None',
  Basic: 'Basic',
  Key: 'API key',
  ManagedServiceIdentity: 'Managed identity',
};

const AUTH_MODES: McpAuthMode[] = ['None', 'Basic', 'Key', 'ManagedServiceIdentity'];

export interface McpAddServerFormProps {
  /**
   * Workflow name used to scope the `listMcpTools` validation probe. When
   * omitted (e.g. the form is opened from the app-level Connections editor,
   * which has no workflow context), the probe is skipped and the endpoint
   * is persisted as-is — the runtime will validate it at execution time.
   */
  workflowName?: string;
  onCancel: () => void;
  /**
   * Fired after a successful save. The `recordKey` is the
   * `agentMcpConnections[key]` slot that was written — the new key in add
   * mode, or the existing key in edit mode.
   */
  onAdded: (recordKey: string) => void;
  /**
   * `'add'` (default) — derive a new slug key from the display name and
   * dedupe against existing entries. `'edit'` — overwrite `initialRecordKey`
   * in place, seed the form from `initialConnection`, and swap chrome text /
   * button labels accordingly.
   */
  mode?: 'add' | 'edit';
  /** Required when `mode === 'edit'`: the key of the record being edited. */
  initialRecordKey?: string;
  /** Required when `mode === 'edit'`: the current connection record. */
  initialConnection?: McpConnection;
  /**
   * Optional persistence override, used only in edit mode. When provided,
   * the form runs its probe as usual but hands off the final record write
   * to this callback instead of writing to `connections.json` directly.
   * This lets the caller route the save through a draft/prod-aware handler
   * (e.g. `useConnectionsEditor.handleSaveLocalConnection`). Errors thrown
   * from the callback surface in the form's error banner.
   */
  onEditSave?: (recordKey: string, record: McpConnection) => Promise<void>;
}

/**
 * Slug-normalize a display name into a stable `agentMcpConnections` record key.
 * Mirrors the LogicAppsUX wizard's key-derivation rule.
 *   - Lowercase, ASCII letters/digits only
 *   - Non-alphanumeric runs collapsed to a single `-`
 *   - Leading/trailing `-` trimmed
 *   - Falls back to `mcp-server` when the result would be empty
 */
function slugifyConnectionKey(displayName: string): string {
  const cleaned = displayName
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return cleaned || 'mcp-server';
}

/**
 * Append `-2`, `-3`, … until the key is unique in `existing`.
 */
function dedupeKey(base: string, existing: Record<string, unknown>): string {
  if (!Object.prototype.hasOwnProperty.call(existing, base)) return base;
  for (let i = 2; i < 1000; i++) {
    const candidate = `${base}-${i}`;
    if (!Object.prototype.hasOwnProperty.call(existing, candidate)) return candidate;
  }
  // Extremely unlikely; fall back to a timestamp-based suffix.
  return `${base}-${Date.now()}`;
}

/**
 * Returns true when `value` is a valid endpoint URL for an MCP server.
 *
 * Rules:
 * - Must be a parseable absolute URL with http:// or https:// scheme.
 * - When `requireSecure` is true, only https:// is allowed unless the host
 *   is a loopback (localhost / 127.0.0.1 / ::1) — useful for local-dev
 *   stacks. Plain http:// with credentials over the wire is rejected so
 *   users don't unintentionally leak Basic/Key/MSI tokens.
 */
function isValidEndpointUrl(value: string, requireSecure: boolean): boolean {
  let parsed: URL;
  try {
    parsed = new URL(value);
  } catch {
    return false;
  }
  if (parsed.protocol === 'https:') return true;
  if (parsed.protocol !== 'http:') return false;
  const host = parsed.hostname.toLowerCase();
  const isLoopback = host === 'localhost' || host === '127.0.0.1' || host === '[::1]' || host === '::1';
  if (requireSecure && !isLoopback) return false;
  return true;
}

/**
 * Detects the VFS RBAC permission failure that the runtime returns when the
 * caller lacks `automationProjects/applications/vfs/read` on the current
 * project. Matches the specific AutomationAuthorizationFailed error code and
 * the `vfs/read` permission marker. This is an Otto limitation (the runtime
 * can still consume the connection at execution time), so the form lets the
 * user save anyway instead of blocking.
 *
 * We intentionally do NOT match a bare "403": a real MCP endpoint that returns
 * 403 on bad credentials must keep blocking the save rather than offering a
 * misleading "save anyway", which would persist invalid credentials.
 */
function isVfsPermissionError(message: string): boolean {
  if (!message) return false;
  const lower = message.toLowerCase();
  return (
    lower.includes('automationauthorizationfailed') ||
    lower.includes('vfs/read')
  );
}

interface AuthFields {
  username: string;
  password: string;
  apiKey: string;
  keyHeaderName: string;
  identity: string;
  audience: string;
}

const EMPTY_AUTH_FIELDS: AuthFields = {
  username: '',
  password: '',
  apiKey: '',
  keyHeaderName: '',
  identity: '',
  audience: '',
};

/**
 * Inverse of `buildAuthentication` — reads a persisted `McpConnection.authentication`
 * blob and returns the `{ mode, fields }` pair to seed the form with when
 * editing an existing record. Unknown / null authentication maps to `'None'`.
 */
function decodeAuthentication(
  authentication: McpConnection['authentication'] | undefined,
): { mode: McpAuthMode; fields: AuthFields } {
  const fields: AuthFields = { ...EMPTY_AUTH_FIELDS };
  if (!authentication || typeof authentication !== 'object') {
    return { mode: 'None', fields };
  }
  const type = (authentication as { type?: unknown }).type;
  const auth = authentication as Record<string, unknown>;
  switch (type) {
    case 'Basic':
      fields.username = typeof auth.username === 'string' ? auth.username : '';
      fields.password = typeof auth.password === 'string' ? auth.password : '';
      return { mode: 'Basic', fields };
    case 'Key':
      fields.apiKey = typeof auth.key === 'string' ? auth.key : '';
      fields.keyHeaderName = typeof auth.keyHeaderName === 'string' ? auth.keyHeaderName : '';
      return { mode: 'Key', fields };
    case 'ManagedServiceIdentity':
      fields.identity = typeof auth.identity === 'string' ? auth.identity : '';
      fields.audience = typeof auth.audience === 'string' ? auth.audience : '';
      return { mode: 'ManagedServiceIdentity', fields };
    default:
      return { mode: 'None', fields };
  }
}

/**
 * Build the `authentication` payload for `listMcpTools` and for the persisted
 * `McpConnection.authentication` record. Returns `undefined` for `None` so the
 * runtime treats the connection as unauthenticated (the native MCP path
 * accepts an omitted `authentication` per the existing
 * `buildListMcpToolsBody` contract).
 */
function buildAuthentication(
  mode: McpAuthMode,
  fields: AuthFields,
): Record<string, unknown> | undefined {
  switch (mode) {
    case 'None':
      return undefined;
    case 'Basic':
      return {
        type: 'Basic',
        username: fields.username,
        password: fields.password,
      };
    case 'Key':
      return {
        type: 'Key',
        key: fields.apiKey,
        keyHeaderName: fields.keyHeaderName.trim() || 'X-API-Key',
      };
    case 'ManagedServiceIdentity': {
      const auth: Record<string, unknown> = { type: 'ManagedServiceIdentity' };
      if (fields.identity.trim()) auth.identity = fields.identity.trim();
      if (fields.audience.trim()) auth.audience = fields.audience.trim();
      return auth;
    }
    default:
      return undefined;
  }
}

export const McpAddServerForm = ({
  workflowName,
  onCancel,
  onAdded,
  mode = 'add',
  initialRecordKey,
  initialConnection,
  onEditSave,
}: McpAddServerFormProps) => {
  const styles = useStyles();
  const { getOttoToken } = useAuth();
  const addNotification = useNotificationStore((state) => state.addNotification);

  const isEdit = mode === 'edit';

  const selectedApp = useAppStore((state) => state.selectedApp);
  const { projectName } = useCurrentProject();

  // Use `selectedApp.name` (the Otto application name) — NOT the ACA
  // container app name from `parseAppId(...)`. See the comment in
  // `useMcpToolsForConnection` for the full rationale; using the wrong
  // segment produces 403 errors on VFS calls.
  const appName = selectedApp?.name ?? '';
  const armResourceId = selectedApp?.armResourceId ?? selectedApp?.properties?.appResourceId ?? '';

  const appInfo = useMemo(
    () =>
      projectName && appName
        ? { projectName, appName, appId: armResourceId }
        : null,
    [projectName, appName, armResourceId],
  );

  // Read existing connections so we can dedupe the new key and round-trip
  // unrelated entries unchanged.
  const connectionsQuery = useConnectionsJson(appInfo);
  const saveConnections = useSaveConnectionsJson(appInfo);
  const mergeMcpConnectionRecords = useConnectionStore((s) => s.mergeMcpConnectionRecords);
  const queryClient = useQueryClient();

  // Seed state from `initialConnection` in edit mode; add mode starts empty.
  // The initial-value memos intentionally depend only on the identity of
  // `initialConnection` — we don't want the seed to change on every render.
  const initialAuth = useMemo(
    () => decodeAuthentication(initialConnection?.authentication),
    [initialConnection],
  );
  const [displayName, setDisplayName] = useState(initialConnection?.displayName ?? '');
  const [endpoint, setEndpoint] = useState(initialConnection?.mcpServerUrl ?? '');
  const [authMode, setAuthMode] = useState<McpAuthMode>(initialAuth.mode);
  const [authFields, setAuthFields] = useState<AuthFields>(initialAuth.fields);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // When the probe fails with a permission error (403), we set a warning
  // instead of an error and let the user opt into saving anyway. The
  // underlying VFS RBAC failure means we can't validate the endpoint here,
  // but the runtime can still consume the saved connection at execution time.
  const [probeWarning, setProbeWarning] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string | undefined>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const setField = useCallback(<K extends keyof AuthFields>(key: K, value: AuthFields[K]) => {
    setAuthFields((prev) => ({ ...prev, [key]: value }));
  }, []);

  // Any change to the probed inputs invalidates a prior probe warning. The
  // user must re-attempt validation against the new endpoint/credentials
  // before we'll let them save anyway.
  useEffect(() => {
    setProbeWarning(null);
  }, [displayName, endpoint, authMode, authFields]);

  const validateForm = useCallback((): boolean => {
    const errors: Record<string, string | undefined> = {};

    if (!displayName.trim()) {
      errors.displayName = 'Display name is required';
    }

    const trimmedEndpoint = endpoint.trim();
    if (!trimmedEndpoint) {
      errors.endpoint = 'Endpoint URL is required';
    } else {
      const requireSecure = authMode !== 'None';
      if (!isValidEndpointUrl(trimmedEndpoint, requireSecure)) {
        errors.endpoint = requireSecure
          ? 'Enter an https:// URL (http:// is only allowed for localhost)'
          : 'Enter a valid http:// or https:// URL';
      }
    }

    if (authMode === 'Basic') {
      if (!authFields.username.trim()) errors.username = 'Username is required';
      if (!authFields.password) errors.password = 'Password is required';
    }
    if (authMode === 'Key') {
      if (!authFields.apiKey) errors.apiKey = 'API key is required';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }, [displayName, endpoint, authMode, authFields]);

  // In edit mode the app-level Connections editor doesn't have a specific
  // workflow to scope the probe to, so we allow submitting without one and
  // skip the probe below. In add mode (rendered from a workflow-scoped
  // panel) the caller always supplies `workflowName`.
  const canSubmit = !isSubmitting && !!appInfo && (isEdit || !!workflowName);

  const handleSubmit = useCallback(
    async (options?: { skipProbe?: boolean }) => {
      const skipProbeArg = options?.skipProbe ?? false;
      // Skip the probe when we have no workflow context to scope it to
      // (edit mode from the Connections editor), or when the caller opted
      // into the "save anyway" path after a VFS-permission warning.
      const skipProbe = skipProbeArg || !workflowName;
      setSubmitError(null);
      if (!skipProbe) {
        // Clear any stale probe warning before re-attempting validation.
        setProbeWarning(null);
      }

      if (!validateForm()) return;
      if (!appInfo) {
        setSubmitError('No app selected — cannot save MCP connection.');
        return;
      }
      if (isEdit && !initialRecordKey) {
        setSubmitError('Missing record key — cannot save edited MCP connection.');
        return;
      }

      const trimmedEndpoint = endpoint.trim();
      const trimmedName = displayName.trim();
      const authentication = buildAuthentication(authMode, authFields);

      setIsSubmitting(true);
      try {
        // 1. Probe the endpoint via the runtime. We surface most failures
        //    inline rather than persisting a known-bad connection. The
        //    exception is a 403 from the VFS RBAC check — that's an Otto
        //    permission limitation, not an issue with the user's endpoint
        //    or credentials, so we let the user opt into saving anyway.
        if (!skipProbe && workflowName) {
          const token = await getOttoToken();
          if (!token) {
            throw new Error('Failed to acquire access token. Please sign in again.');
          }

          try {
            await listMcpTools(
              {
                projectName: appInfo.projectName,
                appName: appInfo.appName,
                workflowName,
                accessToken: token,
              },
              {
                id: 'byo-probe',
                mcpServerUrl: trimmedEndpoint,
                ...(authentication ? { authentication } : {}),
              },
            );
          } catch (err) {
            const message = err instanceof Error ? err.message : String(err);
            if (isVfsPermissionError(message)) {
              setProbeWarning(
                'We could not validate this endpoint because you do not have ' +
                  "permission to run the runtime's list-tools probe on this " +
                  'project. The connection details below will be saved as-is ' +
                  'and the runtime will validate them at workflow execution ' +
                  'time. Click "Save anyway" to proceed.',
              );
              return;
            }
            // Non-permission failure — sanitize (strip URLs / cap length) and surface.
            setSubmitError(sanitizeErrorMessage(message));
            return;
          }
        }

        // 2. Refetch the latest connections.json before computing the record
        //    key so we never clobber a concurrent edit (e.g. another tab
        //    adding a record between our initial fetch and this save). In
        //    add mode we slug + dedupe the display name; in edit mode we
        //    overwrite the existing key.
        //    In edit mode with a custom `onEditSave` we can skip the refetch —
        //    the caller (e.g. `handleSaveLocalConnection`) owns the read-
        //    merge-write cycle against the appropriate draft/prod file.
        let recordKey: string;
        let baseDoc: ConnectionsConfiguration | null = null;
        let existing: Record<string, McpConnection> = {};

        if (isEdit && onEditSave) {
          recordKey = initialRecordKey!;
        } else {
          const refreshed = await connectionsQuery.refetch();
          if (refreshed.isError || !refreshed.data) {
            setSubmitError(
              'Could not load the latest connection list. Refresh and try again.',
            );
            return;
          }
          baseDoc = refreshed.data;
          existing = baseDoc.agentMcpConnections ?? {};
          recordKey = isEdit
            ? initialRecordKey!
            : dedupeKey(slugifyConnectionKey(trimmedName), existing);
        }

        // 3. Build the record and persist. In add mode (or edit mode without
        //    an override) we write to connections.json directly; with
        //    `onEditSave` provided we delegate to the caller.
        const record: McpConnection = {
          mcpServerUrl: trimmedEndpoint,
          displayName: trimmedName,
          authentication: authentication ?? null,
        };

        if (isEdit && onEditSave) {
          await onEditSave(recordKey, record);
        } else if (baseDoc) {
          const next: ConnectionsConfiguration = {
            ...baseDoc,
            agentMcpConnections: {
              ...existing,
              [recordKey]: record,
            },
          };

          await saveConnections.mutateAsync(next);

          // Additively merge the just-persisted BYO connection records into
          // the publish baseline so a subsequent publish keeps them. See the
          // `mergeMcpConnectionRecords` docstring for why we pass the full
          // bucket rather than just the new record. Skipped when the caller
          // owns persistence — they can manage baseline sync themselves.
          mergeMcpConnectionRecords(next.agentMcpConnections ?? {});
        }

        // Drop any cached MCP tool/server data so a future re-add or re-probe
        // of the same endpoint+auth-type can't serve a stale response (the
        // fingerprint key intentionally excludes secrets, so changing only a
        // credential wouldn't otherwise bust the cache).
        await queryClient.invalidateQueries({ queryKey: mcpKeys.all });

        addNotification({
          type: 'success',
          title: isEdit ? 'MCP server updated' : 'MCP server added',
          message: isEdit
            ? `"${trimmedName}" was updated.`
            : `"${trimmedName}" is now available.`,
        });

        onAdded(recordKey);
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        setSubmitError(sanitizeErrorMessage(message));
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      validateForm,
      appInfo,
      workflowName,
      endpoint,
      displayName,
      authMode,
      authFields,
      getOttoToken,
      connectionsQuery,
      saveConnections,
      mergeMcpConnectionRecords,
      queryClient,
      addNotification,
      onAdded,
      isEdit,
      initialRecordKey,
      onEditSave,
    ],
  );

  return (
    <div className={styles.root} role="region" aria-label={isEdit ? 'Edit MCP server' : 'Add MCP server'}>
      <Text className={styles.title}>{isEdit ? 'Edit MCP server' : 'Add MCP server'}</Text>
      <Text className={styles.helpText}>
        {isEdit
          ? workflowName
            ? 'Update the endpoint or credentials. The endpoint will be revalidated before saving.'
            : 'Update the endpoint or credentials. The endpoint will be validated by the runtime at execution time.'
          : 'Register a custom MCP server reachable by the runtime. The endpoint will be validated before saving.'}
      </Text>

      <Field
        label="Display name"
        required
        validationState={fieldErrors.displayName ? 'error' : 'none'}
        {...(fieldErrors.displayName ? { validationMessage: fieldErrors.displayName } : {})}
      >
        <Input
          value={displayName}
          onChange={(_, data) => setDisplayName(data.value)}
          placeholder="My MCP server"
          disabled={isSubmitting}
          aria-label="Display name"
        />
      </Field>

      <Field
        label="Endpoint URL"
        required
        validationState={fieldErrors.endpoint ? 'error' : 'none'}
        {...(fieldErrors.endpoint ? { validationMessage: fieldErrors.endpoint } : {})}
      >
        <Input
          value={endpoint}
          onChange={(_, data) => setEndpoint(data.value)}
          placeholder="https://mcp.example.com/sse"
          disabled={isSubmitting}
          type="url"
          aria-label="Endpoint URL"
        />
      </Field>

      <Field label="Authentication">
        <Dropdown
          value={AUTH_MODE_LABELS[authMode]}
          selectedOptions={[authMode]}
          onOptionSelect={(_, data) => {
            const next = (data.optionValue ?? 'None') as McpAuthMode;
            setAuthMode(next);
            // Reset per-mode field errors so stale validation doesn't linger.
            setFieldErrors((prev) => ({
              ...prev,
              username: undefined,
              password: undefined,
              apiKey: undefined,
            }));
          }}
          disabled={isSubmitting}
          aria-label="Authentication type"
        >
          {AUTH_MODES.map((mode) => (
            <Option key={mode} value={mode} text={AUTH_MODE_LABELS[mode]}>
              {AUTH_MODE_LABELS[mode]}
            </Option>
          ))}
        </Dropdown>
      </Field>

      {authMode === 'Basic' && (
        <>
          <Field
            label="Username"
            required
            validationState={fieldErrors.username ? 'error' : 'none'}
            {...(fieldErrors.username ? { validationMessage: fieldErrors.username } : {})}
          >
            <Input
              value={authFields.username}
              onChange={(_, data) => setField('username', data.value)}
              disabled={isSubmitting}
              autoComplete="off"
              aria-label="Username"
            />
          </Field>
          <Field
            label="Password"
            required
            validationState={fieldErrors.password ? 'error' : 'none'}
            {...(fieldErrors.password ? { validationMessage: fieldErrors.password } : {})}
          >
            <Input
              value={authFields.password}
              onChange={(_, data) => setField('password', data.value)}
              type="password"
              disabled={isSubmitting}
              autoComplete="new-password"
              aria-label="Password"
            />
          </Field>
        </>
      )}

      {authMode === 'Key' && (
        <>
          <Field
            label="API key"
            required
            validationState={fieldErrors.apiKey ? 'error' : 'none'}
            {...(fieldErrors.apiKey ? { validationMessage: fieldErrors.apiKey } : {})}
          >
            <Input
              value={authFields.apiKey}
              onChange={(_, data) => setField('apiKey', data.value)}
              type="password"
              disabled={isSubmitting}
              autoComplete="off"
              aria-label="API key"
            />
          </Field>
          <Field label="Header name" hint="Defaults to X-API-Key">
            <Input
              value={authFields.keyHeaderName}
              onChange={(_, data) => setField('keyHeaderName', data.value)}
              placeholder="X-API-Key"
              disabled={isSubmitting}
              aria-label="Header name"
            />
          </Field>
        </>
      )}

      {authMode === 'ManagedServiceIdentity' && (
        <>
          <Field
            label="Identity"
            hint="Optional. Resource ID of a user-assigned managed identity."
          >
            <Input
              value={authFields.identity}
              onChange={(_, data) => setField('identity', data.value)}
              placeholder="/subscriptions/…/userAssignedIdentities/<name>"
              disabled={isSubmitting}
              aria-label="Identity"
            />
          </Field>
          <Field
            label="Audience"
            hint="Optional. Token audience (defaults to the server endpoint)."
          >
            <Input
              value={authFields.audience}
              onChange={(_, data) => setField('audience', data.value)}
              placeholder="https://mcp.example.com"
              disabled={isSubmitting}
              aria-label="Audience"
            />
          </Field>
        </>
      )}

      {probeWarning && (
        <div className={styles.warnBanner} role="status">
          {probeWarning}
        </div>
      )}

      {submitError && (
        <div className={styles.errorBanner} role="alert">
          {submitError}
        </div>
      )}

      <div className={styles.actions}>
        <Button appearance="secondary" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          appearance="primary"
          onClick={() => handleSubmit({ skipProbe: !!probeWarning })}
          disabled={!canSubmit}
        >
          {isSubmitting
            ? probeWarning || !workflowName
              ? 'Saving…'
              : 'Validating…'
            : probeWarning
              ? 'Save anyway'
              : isEdit
                ? 'Save'
                : 'Add server'}
        </Button>
      </div>
    </div>
  );
};
